import mitt, { Emitter } from 'mitt';
import to from 'await-to-js';
import { getApi, getEnv, getClientId } from './init';
import axios from 'axios';

export enum AUTH_MANAGER_EVENT {
  REFRESH_TOKEN = 'refresh:token',
  LOGOUT_TOKEN = 'logout:token',
  CHANGE_TOKEN = 'change:token',
}

interface IToken {
  accessToken: string;
  accessTokenExpireIn: number;
  refreshToken: string;
  refreshTokenExpireIn: number;
  createdAt: number;
}

type IEventBody = Record<AUTH_MANAGER_EVENT, IToken | null>;

const ENV_URL = {
  local: {
    SSO_URL: 'https://sso-dev.lingvisions.com',
  },
  development: {
    SSO_URL: 'https://sso-dev.lingvisions.com',
  },
  fat: {
    SSO_URL: 'https://sso-fat.lingvisions.com',
  },
  production: {
    SSO_URL: 'https://sso.lingvisions.com',
  },
};

class AuthManager {
  private _eventBus: Emitter<IEventBody> = mitt();
  private _refreshing = false;
  tokenKey = 'token';

  private _emit<K extends AUTH_MANAGER_EVENT>(type: K, data: IEventBody[K]) {
    this._eventBus.emit(type, data);
  }

  on<K extends AUTH_MANAGER_EVENT>(type: K, handler: (data: IEventBody[K]) => void) {
    const fn = (args: any) => {
      handler(args);
    };
    this._eventBus.on(type, fn);
  }

  once<K extends AUTH_MANAGER_EVENT>(type: K, handler: (data: IEventBody[K]) => void) {
    const fn = (args: any) => {
      this._eventBus.off(type, fn);
      handler(args);
    };
    this._eventBus.on(type, fn);
  }

  set(params: { tokenKey: string }) {
    const { tokenKey = this.tokenKey } = params;
    this.tokenKey = tokenKey;
  }

  public async setToken(token: IToken) {
    localStorage.setItem(this.tokenKey, JSON.stringify(token));
  }

  public async getToken(): Promise<IToken | null> {
    const result = localStorage.getItem(this.tokenKey);
    return result ? JSON.parse(result) : null;
  }

  public async removeToken() {
    localStorage.removeItem(this.tokenKey);
  }

  // 通过code实现静默登录
  async silentLogin(): Promise<string | null> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');

    // 清空url上code
    url.searchParams.delete('code');
    window.history.replaceState({}, document.title, url.toString());

    if (code) {
      const { ucLoginApi } = getApi();
      const [err, res] = await to(
        ucLoginApi.codeToToken({
          authorizationCode: code,
          clientId: getClientId(),
        }),
      );

      if (!err) {
        const token = {
          ...res,
          createdAt: Date.now(),
        };
        await this._setToken(token as IToken);
        return token.accessToken;
      }
    }

    const accessToken = await this.getAccessToken();
    return accessToken;
  }

  private async _setToken(token: IToken) {
    await this.setToken(token);
    this._emit(AUTH_MANAGER_EVENT.CHANGE_TOKEN, token);
  }

  // 单点登录
  login({
    redirectUri = '',
    authUrl = '/aiad/authorization',
    loginPageUri = '',
    isJump = true,
    windowOpen = false,
  }) {
    const state = redirectUri ? `&state=redirectUri=${encodeURIComponent(redirectUri)}` : '';

    const ssoUrl = `${ENV_URL[getEnv()].SSO_URL}/api/login/authorize`;
    const ssoQueryStr = `responseType=code&clientId=${getClientId()}&loginPageUri=${encodeURIComponent(
      loginPageUri,
    )}&redirectUri=${encodeURIComponent(
      authUrl.startsWith('http') ? authUrl : location.origin + authUrl,
    )}${state}`;

    if (isJump) {
      const url = `${ssoUrl}?${ssoQueryStr}`;
      if (windowOpen) {
        window.open(url);
      } else {
        location.href = url;
      }
    }

    return {
      ssoUrl,
      ssoQueryStr,
    };
  }

  // 退出登录
  async logout({ redirectUri = '', authUrl = '/aiad/authorization' }) {
    const token = await this.getToken();
    const state = redirectUri ? `&state=redirectUri=${encodeURIComponent(redirectUri)}` : '';
    const SSO_URL = `${ENV_URL[getEnv()].SSO_URL}/api/login/logout?token=${
      token?.accessToken || ''
    }&redirectUri=${encodeURIComponent(
      `${ENV_URL[getEnv()].SSO_URL}?redirectUri=${encodeURIComponent(
        location.origin + authUrl,
      )}&clientId=${getClientId()}&${state}`,
    )}`;

    await this.removeToken();
    location.replace(SSO_URL);
  }

  async getAccessToken(): Promise<string | null> {
    const token = await this.getToken();

    if (!token) return null;

    // 距离过期时间小于5分钟，需要刷新
    const isNeedRefresh =
      token.accessTokenExpireIn - (Date.now() - token.createdAt) / 1000 <= 5 * 60;
    if (isNeedRefresh) {
      return new Promise(async (resolve) => {
        function handle(token: IToken | null) {
          resolve(token ? token.accessToken : null);
        }
        this.once(AUTH_MANAGER_EVENT.REFRESH_TOKEN, handle);

        if (!this._refreshing) {
          this._refreshing = true;
          const [err, res] = await to(
            axios.post('/api/uc/login/refresh', {
              refreshToken: token?.refreshToken,
            }),
          );
          this._refreshing = false;

          if (err) {
            this._emit(AUTH_MANAGER_EVENT.REFRESH_TOKEN, null);
          } else {
            const token = {
              ...res.data.result,
              createdAt: Date.now(),
            };
            this._emit(AUTH_MANAGER_EVENT.REFRESH_TOKEN, token as IToken);
            await this._setToken(token);
          }
        }
      });
    } else {
      return token.accessToken;
    }
  }

  // 切换组织
  async selectOrg(orgId: string) {
    const token = await this.getToken();
    if (token) {
      const { ucOrgsApi } = getApi();
      await ucOrgsApi.selectOrg({
        orgId,
        refreshToken: token.refreshToken,
        accessToken: token.accessToken,
      });
    }
  }
}

let authManager;
export function getAuthManager() {
  if (!authManager) {
    authManager = new AuthManager();
  }
  return authManager;
}
